import ImageUpload from './ImageUpload.jsx';
import { getOrgId } from '../lib/constants.js';
import { getTaxConfig } from '../lib/taxConfig.js';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { T, totalLead, rop, safetyStock, eoq, Inp, Sel, Field, Btn, Cap } from '../lib/ui.jsx';

// Taxonomía de categorías (tabla `categories`) — la MISMA fuente de verdad que
// gestiona CategoriasManager. El form la lee directo para que el dropdown de
// subcategorías NO dependa de qué productos alcanzaron a cargar en memoria (esa
// dependencia hacía que "según cuándo inicies se vieran menos subcategorías").
function _sessionToken() {
  try { return JSON.parse(localStorage.getItem('aryes-session') || 'null')?.access_token || ''; }
  catch { return ''; }
}

// Combobox estilo Shopify: elegí una opción ya creada (flecha ▾ + lista visible,
// filtra a medida que tipeás) o creá una nueva inline ("Crear …"). Reemplaza al
// <datalist> nativo, que no mostraba ninguna pista de que había opciones para elegir.
const Combobox=({value,onChange,options=[],placeholder,disabled,createLabel="Crear"})=>{
  const [open,setOpen]=useState(false);
  const [focusIdx,setFocusIdx]=useState(-1);
  const wrapRef=useRef(null);
  const val=value||"";
  const q=val.trim().toLowerCase();
  const filtered=options.filter(o=>o.toLowerCase().includes(q));
  const exact=options.some(o=>o.toLowerCase()===q);
  const showCreate=val.trim()!==""&&!exact;
  React.useEffect(()=>{
    if(!open)return;
    const onDoc=(e)=>{if(wrapRef.current&&!wrapRef.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",onDoc);
    return()=>document.removeEventListener("mousedown",onDoc);
  },[open]);
  const pick=(v)=>{onChange(v);setOpen(false);setFocusIdx(-1);};
  return(
    <div ref={wrapRef} style={{position:"relative"}}>
      <div style={{display:"flex",alignItems:"center",boxSizing:"border-box",background:disabled?"#f5f5f0":T.card,border:`1px solid ${open?T.green:T.border}`,borderRadius:4,transition:"border-color .12s"}}>
        <input value={val} disabled={disabled}
          onChange={e=>{onChange(e.target.value);if(!open)setOpen(true);setFocusIdx(-1);}}
          onFocus={()=>{if(!disabled)setOpen(true);}}
          onKeyDown={e=>{
            if(e.key==="Escape"){setOpen(false);}
            else if(e.key==="ArrowDown"){e.preventDefault();setOpen(true);setFocusIdx(i=>Math.min(i+1,filtered.length-1));}
            else if(e.key==="ArrowUp"){e.preventDefault();setFocusIdx(i=>Math.max(i-1,0));}
            else if(e.key==="Enter"&&open&&focusIdx>=0&&focusIdx<filtered.length){e.preventDefault();pick(filtered[focusIdx]);}
          }}
          placeholder={placeholder}
          style={{flex:1,minWidth:0,border:"none",outline:"none",background:"transparent",fontFamily:T.sans,fontSize:13,color:T.text,padding:"9px 11px"}}/>
        <button type="button" tabIndex={-1} disabled={disabled} aria-label="Mostrar opciones"
          onMouseDown={e=>e.preventDefault()} onClick={()=>{if(!disabled)setOpen(o=>!o);}}
          style={{border:"none",background:"transparent",cursor:disabled?"default":"pointer",padding:"0 9px",color:T.textSm,fontSize:11,transform:open?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</button>
      </div>
      {open&&!disabled&&(filtered.length>0||showCreate)&&(
        <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:50,marginTop:4,background:"#fff",border:`1px solid ${T.border}`,borderRadius:6,boxShadow:"0 6px 24px rgba(0,0,0,.12)",maxHeight:220,overflowY:"auto"}}>
          {filtered.map((o,i)=>(
            <div key={o} onMouseDown={e=>{e.preventDefault();pick(o);}} onMouseEnter={()=>setFocusIdx(i)}
              style={{padding:"8px 11px",fontFamily:T.sans,fontSize:13,color:T.text,cursor:"pointer",background:i===focusIdx?T.hover:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span>{o}</span>
              {q===o.toLowerCase()&&<span style={{color:T.green,fontWeight:700}}>✓</span>}
            </div>
          ))}
          {showCreate&&(
            <div onMouseDown={e=>{e.preventDefault();pick(val.trim());}}
              style={{padding:"8px 11px",fontFamily:T.sans,fontSize:13,cursor:"pointer",borderTop:filtered.length?`1px solid ${T.border}`:"none",color:T.green,fontWeight:600}}>
              {createLabel} “{val.trim()}”
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductForm=({product,suppliers,onSave,onClose,brandCfg,categories=[],subcatsByCat={}})=>{
  const taxCfg=getTaxConfig(brandCfg?.tax_country||"UY");
  const blank={name:"",codigo:"",barcode:"",supplierId:"",unit:"kg",stock:0,unitCost:0,precioVenta:0,iva_rate:taxCfg.defaultRate,imagen_url:"",descripcion:"",history:[],volume_tiers:[],variants:{label:"Color",options:[]},atributos:[]};
  const normVariants=(v)=>{const o=v&&typeof v==="object"&&!Array.isArray(v)?v:{};return{label:o.label??"Color",options:Array.isArray(o.options)?o.options:[]};};
  const [f,setF]=useState(product?{...product,volume_tiers:Array.isArray(product.volume_tiers)?product.volume_tiers:[],variants:normVariants(product.variants),atributos:Array.isArray(product.atributos)?product.atributos:[]}:blank);

  // Filas de la taxonomía: [{id,nombre,parent_id}]. Se cargan una vez al abrir el
  // form. Si falla (sesión, red), quedamos con lo derivado de productos (props).
  const [taxRows,setTaxRows]=useState([]);
  useEffect(()=>{
    let vivo=true;
    const tk=_sessionToken();
    if(!tk)return;
    fetch('/api/categories?action=list',{headers:{Authorization:`Bearer ${tk}`}})
      .then(r=>r.ok?r.json():[])
      .then(rows=>{if(vivo&&Array.isArray(rows))setTaxRows(rows);})
      .catch(()=>{});
    return()=>{vivo=false;};
  },[]);

  // Categorías = unión (taxonomía madre + las derivadas de productos por props).
  const cats=useMemo(()=>{
    const set=new Map(); // lower -> label (preserva el casing de la taxonomía)
    for(const c of taxRows){ if(!c.parent_id&&c.nombre){const k=c.nombre.trim().toLowerCase();if(!set.has(k))set.set(k,c.nombre.trim());} }
    for(const c of categories){ if(c){const k=String(c).trim().toLowerCase();if(!set.has(k))set.set(k,String(c).trim());} }
    return [...set.values()].sort((a,b)=>a.localeCompare(b,'es'));
  },[taxRows,categories]);

  // Subcategorías por categoría = unión (subcats de la taxonomía bajo esa madre +
  // las que ya usan los productos). Dedup case-insensitive, sin depender de la carga.
  const subsByCat=useMemo(()=>{
    const madreById=new Map(); // id -> nombre madre
    for(const c of taxRows){ if(!c.parent_id&&c.nombre)madreById.set(c.id,c.nombre.trim()); }
    const out={}; // catLower -> Map(subLower->label)
    const add=(cat,sub)=>{ if(!cat||!sub)return; const ck=String(cat).trim().toLowerCase(); const sk=String(sub).trim().toLowerCase(); (out[ck]=out[ck]||new Map()); if(!out[ck].has(sk))out[ck].set(sk,String(sub).trim()); };
    for(const c of taxRows){ if(c.parent_id){const madre=madreById.get(c.parent_id); add(madre,c.nombre);} }
    for(const [cat,set] of Object.entries(subcatsByCat||{})){ for(const s of set) add(cat,s); }
    // resolver para la categoría elegida por su nombre (case-insensitive)
    return (catName)=>{ const ck=String(catName||'').trim().toLowerCase(); return out[ck]?[...out[ck].values()].sort((a,b)=>a.localeCompare(b,'es')):[]; };
  },[taxRows,subcatsByCat]);

  // WA template in localStorage
  // wa template — read from localStorage, not used in current form UI
  const [_csvMsg, setCsvMsg] = useState('');
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const [csv,setCsv]=useState(product?.history?.map(h=>`${h.month},${h.consumed}`).join("\n")||"");
  const bRef=useRef();
  const parseCSV=()=>{
    try{
      const rows=csv.trim().split("\n").map(r=>{const[m,c]=r.split(",");return{month:m?.trim(),consumed:+c?.trim()};}).filter(r=>r.month&&!isNaN(r.consumed)&&r.consumed>0);
      if(!rows.length){setCsvMsg("Sin datos válidos. Formato: YYYY-MM,cantidad");return;}
      setF(p=>({...p,history:rows}));
    }catch{setCsvMsg("Formato incorrecto. Usar: YYYY-MM,cantidad");}
  };
  // Los descuentos por volumen y por caja ahora se cargan por producto DENTRO de
  // cada lista de precios (modelo v2). Acá solo se preserva lo que ya tenía el
  // producto (volume_tiers) para no perder datos al editar; ya no se edita acá.
  const cleanTiers=(arr)=>(Array.isArray(arr)?arr:[])
    .map(t=>({min:Math.floor(Number(t.min)),dto:Number(t.dto)}))
    .filter(t=>Number.isFinite(t.min)&&t.min>1&&Number.isFinite(t.dto)&&t.dto>0&&t.dto<=100)
    .sort((a,b)=>a.min-b.min);

  // Variantes ({label, options:[{id,label,sku,color_hex}]}). Comparten precio/stock
  // del padre; el cliente las elige en el portal. Genérico (colores, sabores, talles).
  const variants=normVariants(f.variants);
  const setVarLabel=(v)=>setF(p=>({...p,variants:{...normVariants(p.variants),label:v}}));
  const addVar=()=>setF(p=>{const cur=normVariants(p.variants);return{...p,variants:{...cur,options:[...cur.options,{id:"",label:"",sku:"",color_hex:""}]}};});
  const updVar=(i,k,v)=>setF(p=>{const cur=normVariants(p.variants);const o=[...cur.options];o[i]={...o[i],[k]:v};return{...p,variants:{...cur,options:o}};});
  const rmVar=(i)=>setF(p=>{const cur=normVariants(p.variants);return{...p,variants:{...cur,options:cur.options.filter((_,j)=>j!==i)}};});
  // Reordenar variantes arrastrando desde el agarre (⠿). El orden del array es el
  // orden en que el cliente las ve en el portal, así que reordenar acá = reordenar
  // en el portal. Pointer events → anda igual en mouse y touch.
  const reorderVar=(from,to)=>setF(p=>{const cur=normVariants(p.variants);const o=[...cur.options];if(from<0||from>=o.length||to<0||to>=o.length||from===to)return p;const[m]=o.splice(from,1);o.splice(to,0,m);return{...p,variants:{...cur,options:o}};});
  const [dragIdx,setDragIdx]=useState(null);
  const rowRefs=useRef([]);
  const onHandleDown=(i)=>(e)=>{e.preventDefault();setDragIdx(i);try{e.target.setPointerCapture(e.pointerId);}catch{/* noop */}};
  const onHandleMove=(e)=>{
    if(dragIdx===null)return;
    const y=e.clientY;
    let target=rowRefs.current.length-1;
    for(let j=0;j<rowRefs.current.length;j++){const el=rowRefs.current[j];if(!el)continue;const r=el.getBoundingClientRect();if(y<r.top+r.height/2){target=j;break;}}
    if(target!==dragIdx){reorderVar(dragIdx,target);setDragIdx(target);}
  };
  const onHandleUp=(e)=>{setDragIdx(null);try{e.target.releasePointerCapture(e.pointerId);}catch{/* noop */}};
  const cleanVariants=(v)=>{const cur=normVariants(v);const seen=new Set();const options=cur.options
    .map(o=>{const label=String(o.label||"").trim();if(!label)return null;let base=String(o.id||label).trim().toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"")||label;
      // Si el slug ya existe (dos labels que colapsan al mismo id, ej "Frutilla"
      // y "Frutilla "), lo desambiguamos con un sufijo en vez de DESCARTAR la
      // variante — antes se perdían silenciosamente y solo se guardaba una.
      let id=base,n=2;while(seen.has(id)){id=`${base}-${n++}`;}seen.add(id);
      return{id,label,sku:String(o.sku||"").trim(),color_hex:/^#[0-9a-fA-F]{6}$/.test(String(o.color_hex||""))?o.color_hex:""};})
    .filter(Boolean);
    return options.length?{label:String(cur.label||"Variante").trim()||"Variante",options}:{};};

  // Atributos flexibles (ficha técnica multi-rubro): lista libre clave/valor propia
  // del rubro del distribuidor. Ej. vino → "Graduación: 12%"; ferretería → "Material:
  // Acero". Se rinden en la ficha del portal. Genérico: sin esquema fijo por vertical.
  const atributos=Array.isArray(f.atributos)?f.atributos:[];
  const addAttr=()=>setF(p=>({...p,atributos:[...(Array.isArray(p.atributos)?p.atributos:[]),{k:"",v:""}]}));
  const updAttr=(i,key,val)=>setF(p=>{const a=[...(Array.isArray(p.atributos)?p.atributos:[])];a[i]={...a[i],[key]:val};return{...p,atributos:a};});
  const rmAttr=(i)=>setF(p=>({...p,atributos:(Array.isArray(p.atributos)?p.atributos:[]).filter((_,j)=>j!==i)}));
  const cleanAttrs=(arr)=>(Array.isArray(arr)?arr:[]).map(a=>({k:String(a.k||"").trim(),v:String(a.v||"").trim()})).filter(a=>a.k&&a.v);

  const sup=suppliers.find(s=>s.id===f.supplierId);
  const lead=sup?totalLead(sup):0;
  const r=f.history.length?rop(f.history,lead):null;
  const ss=f.history.length?safetyStock(f.history,lead):null;
  const eq=f.history.length&&f.unitCost?eoq(f.history,f.unitCost):null;
  return(
    <div style={{display:"grid",gap:16}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <Field label="Nombre del producto"><Inp value={f.name} onChange={e=>set("name",e.target.value)} placeholder="Ej: Harina 000"/></Field>
        <Field label="Código / SKU"><Inp value={f.codigo||""} onChange={e=>set("codigo",e.target.value)} placeholder="Ej: QCO-001"/></Field>
        <Field label="Código de barras (EAN)">
          <div style={{display:"flex",gap:6}}>
            <Inp inputRef={bRef} value={f.barcode} onChange={e=>set("barcode",e.target.value)} placeholder="Escanear o tipear"/>
            <button onClick={()=>bRef.current?.focus()} style={{border:`1px solid ${T.border}`,background:T.muted,padding:"0 10px",cursor:"pointer",fontSize:15,color:T.textSm,borderRadius:4}} title="Foco para scanner">▦</button>
          </div>
        </Field>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
        <Field label="Proveedor / Origen">
          <Sel value={f.supplierId} onChange={e=>set("supplierId",e.target.value)}>
            <option value="">— Seleccioná un proveedor —</option>
            {suppliers.map(s=><option key={s.id} value={s.id}>[{s.flag}] {s.name} — {totalLead(s)}d</option>)}
          </Sel>
          {!suppliers.length && <p style={{fontFamily:T.sans,fontSize:11,color:T.textXs,marginTop:4}}>Aún no cargaste proveedores. Creá uno desde la sección Proveedores.</p>}
        </Field>
        <Field label="Unidad"><Inp value={f.unit} onChange={e=>set("unit",e.target.value)} placeholder="kg, lt, u..."/></Field>
        <Field label="Categoría" hint="Agrupa el producto en el portal. Elegí una existente o escribí una nueva. Vacío = sin categoría.">
          <Combobox value={f.category||""} onChange={v=>set("category",v)} options={cats} placeholder="Elegí o escribí una categoría"/>
        </Field>
        <Field label="Subcategoría" hint="Opcional. Sub-nivel dentro de la categoría (ej: dentro de Bebidas → Gaseosas). Gestioná las opciones desde el botón Categorías.">
          <Combobox value={f.subcategoria||""} onChange={v=>set("subcategoria",v)} options={subsByCat(f.category)}
            placeholder={f.category?"Elegí o escribí una subcategoría":"Elegí primero una categoría"} disabled={!f.category}/>
        </Field>
        <Field label={"Costo unitario (" + taxCfg.currency + ")"}>
          <Inp type="number" step="0.001" min="0" placeholder="0.000" value={f.unitCost||""} onChange={e=>set("unitCost",e.target.value===""?0:+e.target.value)}/>
          {f.costSource&&<div style={{fontFamily:T.sans,fontSize:10,color:T.green,marginTop:4,fontWeight:600}}>⚡ {f.costSource}{f.costUpdatedAt?' · '+new Date(f.costUpdatedAt).toLocaleDateString('es',{day:'2-digit',month:'short'}):''}</div>}
        </Field>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <Field label={"Precio de venta (" + taxCfg.currency + ")"}>
          <Inp type="number" step="0.001" min="0" placeholder="0.000" value={f.precioVenta||f.precio_venta||""} onChange={e=>set("precioVenta",e.target.value===""?0:+e.target.value)}/>
        </Field>
        <Field label={taxCfg.taxName} hint={"Tasa de " + taxCfg.taxName + " (" + taxCfg.country + ")"}>
          <Sel value={f.iva_rate!=null?f.iva_rate:taxCfg.defaultRate} onChange={e=>set("iva_rate",+e.target.value)}>
            {taxCfg.rates.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
            {!taxCfg.rates.some(r=>r.value===(f.iva_rate||0))&&<option value={f.iva_rate}>{f.iva_rate}% — Personalizado</option>}
          </Sel>
          <input type="number" min={0} max={99} step={0.5} value={f.iva_rate!=null?f.iva_rate:taxCfg.defaultRate}
            onChange={e=>set("iva_rate",+e.target.value)}
            style={{width:70,padding:"5px 8px",borderRadius:6,border:"1px solid #e2e2de",fontSize:12,marginTop:4,textAlign:"center"}}
            placeholder="%"/>
        </Field>
      </div>
      <Field label="Foto del producto">
        <ImageUpload value={f.imagen_url||""} onChange={function(url){set("imagen_url",url);}} orgId={getOrgId()} />
      </Field>
      <Field label="Descripción / Ficha técnica" hint="Opcional — aparece en el catálogo al hacer click en el producto">
        <textarea value={f.descripcion||""} onChange={e=>set("descripcion",e.target.value)}
          placeholder="Usos, presentaciones, características técnicas, envases disponibles..."
          style={{width:"100%",minHeight:80,fontFamily:"inherit",fontSize:13,border:`1px solid ${T.border}`,borderRadius:6,padding:"9px 11px",resize:"vertical",background:T.muted,color:T.text,boxSizing:"border-box"}}/>
      </Field>
      <Field label="Unidades por caja" hint="Opcional — cuántas unidades trae una caja cerrada. Es solo el dato físico; el descuento por caja se configura en cada lista de precios.">
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontFamily:T.sans,fontSize:12,color:T.textSm}}>Caja de</span>
          <input type="number" min={0} step={1} value={f.unidades_por_caja||""} onChange={e=>set("unidades_por_caja",e.target.value===""?0:Math.max(0,Math.floor(+e.target.value)))} placeholder="6"
            style={{width:80,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:13,background:T.muted,color:T.text}}/>
          <span style={{fontFamily:T.sans,fontSize:12,color:T.textSm}}>unidades</span>
        </div>
      </Field>
      <Field label="Variantes" hint="Opcional — opciones que el cliente elige en el portal (colores, sabores, talles...). Comparten precio, IVA y stock de este producto. Ej: un colorante con 16 colores en una sola card.">
        <div style={{display:"grid",gap:8}}>
          {variants.options.length>0&&(
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontFamily:T.sans,fontSize:12,color:T.textSm}}>Etiqueta del grupo</span>
              <Inp value={variants.label} onChange={e=>setVarLabel(e.target.value)} placeholder="Color"/>
            </div>
          )}
          {variants.options.length===0&&<p style={{fontFamily:T.sans,fontSize:11,color:T.textXs,margin:0}}>Sin variantes. Agregá una para que el cliente elija (ej: colores) en una sola card.</p>}
          {variants.options.map((o,i)=>(
            <div key={i} ref={el=>{rowRefs.current[i]=el;}} style={{display:"flex",gap:8,alignItems:"center",opacity:dragIdx===i?0.5:1,background:dragIdx===i?T.hover:"transparent",borderRadius:6,transition:"opacity .1s"}}>
              <button onPointerDown={onHandleDown(i)} onPointerMove={onHandleMove} onPointerUp={onHandleUp} title="Arrastrar para reordenar"
                style={{border:`1px solid ${T.border}`,background:T.muted,color:T.textSm,borderRadius:6,width:24,height:34,cursor:dragIdx===i?"grabbing":"grab",fontSize:14,lineHeight:1,padding:0,flexShrink:0,touchAction:"none",display:"flex",alignItems:"center",justifyContent:"center"}}>⠿</button>
              <input type="color" value={o.color_hex||"#cccccc"} onChange={e=>updVar(i,"color_hex",e.target.value)} title="Color (opcional)"
                style={{width:34,height:34,padding:0,border:`1px solid ${T.border}`,borderRadius:6,background:T.muted,cursor:"pointer",flexShrink:0}}/>
              <input value={o.label} onChange={e=>updVar(i,"label",e.target.value)} placeholder="Nombre (ej: Rojo)"
                style={{flex:1,minWidth:0,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:13,background:T.muted,color:T.text}}/>
              <input value={o.sku} onChange={e=>updVar(i,"sku",e.target.value)} placeholder="SKU (opcional)"
                style={{width:120,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:13,background:T.muted,color:T.text}}/>
              <button onClick={()=>rmVar(i)} title="Quitar variante" style={{border:`1px solid ${T.border}`,background:T.muted,color:T.red,borderRadius:6,width:30,height:30,cursor:"pointer",fontSize:16,lineHeight:1,flexShrink:0}}>×</button>
            </div>
          ))}
          <Btn onClick={addVar} variant="ghost" small>+ Agregar variante</Btn>
        </div>
      </Field>
      <Field label="Especificaciones (ficha técnica)" hint="Opcional — datos propios de tu rubro que se muestran como tabla en la ficha del producto (ej: Graduación → 12%, Material → Acero inox, Rendimiento → 40 m²). Una fila por dato.">
        <div style={{display:"grid",gap:8}}>
          {atributos.length===0&&<p style={{fontFamily:T.sans,fontSize:11,color:T.textXs,margin:0}}>Sin especificaciones. Agregá filas clave/valor propias de tu rubro (vino, ferretería, cosmética...).</p>}
          {atributos.map((a,i)=>(
            <div key={i} style={{display:"flex",gap:8,alignItems:"center"}}>
              <input value={a.k||""} onChange={e=>updAttr(i,"k",e.target.value)} placeholder="Dato (ej: Graduación)"
                style={{flex:1,minWidth:0,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:13,background:T.muted,color:T.text}}/>
              <span style={{fontFamily:T.sans,fontSize:13,color:T.textSm,flexShrink:0}}>→</span>
              <input value={a.v||""} onChange={e=>updAttr(i,"v",e.target.value)} placeholder="Valor (ej: 12%)"
                style={{flex:1,minWidth:0,padding:"7px 9px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:13,background:T.muted,color:T.text}}/>
              <button onClick={()=>rmAttr(i)} title="Quitar especificación" style={{border:`1px solid ${T.border}`,background:T.muted,color:T.red,borderRadius:6,width:30,height:30,cursor:"pointer",fontSize:16,lineHeight:1,flexShrink:0}}>×</button>
            </div>
          ))}
          <Btn onClick={addAttr} variant="ghost" small>+ Agregar especificación</Btn>
        </div>
      </Field>
      <Field label="Stock actual"><Inp type="number" min="0" placeholder="0" value={f.stock||""} onChange={e=>set("stock",e.target.value===""?0:+e.target.value)}/></Field>
      {r!==null&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:1,background:T.border,borderRadius:6,overflow:"hidden"}}>
          {[{l:"Punto de pedido (ROP)",v:`${r} ${f.unit}`,h:"Pedir cuando llegue a este nivel"},{l:"Stock de seguridad",v:`${ss} ${f.unit}`,h:"Buffer 95% nivel de servicio"},{l:"Cantidad óptima (EOQ)",v:eq?`${eq} ${f.unit}`:"—",h:"Minimiza costos"}].map((s,i)=>(
            <div key={i} style={{background:T.cardWarm,padding:"12px 14px"}}>
              <Cap>{s.l}</Cap>
              <div style={{fontFamily:T.serif,fontSize:20,fontWeight:500,color:T.text,margin:"4px 0 2px"}}>{s.v}</div>
              <p style={{fontFamily:T.sans,fontSize:10,color:T.textXs}}>{s.h}</p>
            </div>
          ))}
        </div>
      )}
      {f.unitCost>0&&f.precioVenta>0&&(()=>{
        const margen=((f.precioVenta-f.unitCost)/f.precioVenta*100);
        const color=margen<0?T.red:margen<15?T.amber:T.green;
        const bgColor=margen<0?T.redBg:margen<15?T.amberBg:T.greenBg;
        return(
          <div style={{background:bgColor,border:`1px solid ${margen<0?T.redBd:margen<15?'#fde68a':T.greenBd}`,borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{fontFamily:T.sans,fontSize:12,color,fontWeight:600}}>Margen estimado</div>
            <div style={{display:'flex',gap:20,alignItems:'center'}}>
              <span style={{fontFamily:T.sans,fontSize:13,color,fontWeight:700}}>{margen.toFixed(1)}%</span>
              <span style={{fontFamily:T.sans,fontSize:12,color:T.textSm}}>Ganancia: {taxCfg.currencySymbol}{(f.precioVenta-f.unitCost).toLocaleString('es',{minimumFractionDigits:2,maximumFractionDigits:3})} / {f.unit||'u'}</span>
            </div>
          </div>
        );
      })()}
      <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14}}>
        <Field label="Historial de consumo mensual" hint="Pegá desde Excel: YYYY-MM,cantidad — una línea por mes. Más historial = cálculos más precisos.">
          <textarea value={csv} onChange={e=>setCsv(e.target.value)} placeholder={"2024-09,410\n2024-10,420\n2024-11,380\n2024-12,460\n2025-01,410\n2025-02,430"}
            style={{width:"100%",height:90,fontFamily:"monospace",fontSize:12,color:T.text,background:T.muted,border:`1px solid ${T.border}`,padding:"9px 11px",resize:"vertical",borderRadius:4,marginTop:5}}/>
          <div style={{display:"flex",gap:10,marginTop:8,alignItems:"center"}}>
            <Btn onClick={parseCSV}  variant="ghost" small>Importar historial</Btn>
            {f.history.length>0&&<span style={{fontFamily:T.sans,fontSize:11,color:T.ok}}>✓ {f.history.length} meses · prom. {Math.round(f.history.reduce((s,h)=>s+h.consumed,0)/f.history.length)} {f.unit}/mes</span>}
          </div>
        </Field>
      </div>
      <div style={{display:"flex",gap:10,paddingTop:4}}>
        <Btn onClick={()=>onSave({...f,volume_tiers:cleanTiers(f.volume_tiers),variants:cleanVariants(f.variants),atributos:cleanAttrs(f.atributos)})} full>{product?"Guardar cambios":"Agregar producto"}</Btn>
        <Btn onClick={onClose} variant="ghost">Cancelar</Btn>
      </div>
    </div>
  );
};

export default ProductForm;
